import { useCallback, useEffect, useRef, useState } from 'react';

import { POLL_LIMIT_MS, pollDelay } from './polling';

/** A `refetchInterval` for TanStack Query: what it passes is a `Query`, and only its state is read. */
export type PollInterval<TData> = (query: {
  state: { dataUpdateCount: number; data: TData | undefined };
}) => number | false;

/**
 * A polling window for one piece of asynchronous work (`key`: a question, an analysis…), for TanStack Query's
 * `refetchInterval`: it follows `pollDelay`, counts its polls from the window's start, and after `POLL_LIMIT_MS`
 * stops and reports `stillWorking` until `restart()` opens a new window (then `refetch()` the query to start asking).
 */
export function usePollWindow<TData>(
  key: string | null,
  active: boolean,
  pollAfter: (data: TData | undefined) => number | null | undefined,
) {
  const startedAt = useRef(0);
  const firstCount = useRef<number | null>(null);
  const [round, setRound] = useState(0);
  const [expired, setExpired] = useState<string | null>(null);
  const windowId = `${key ?? ''}:${round}`;

  useEffect(() => {
    if (!key || !active) {
      return;
    }
    startedAt.current = Date.now();
    firstCount.current = null;
    const timer = window.setTimeout(() => setExpired(windowId), POLL_LIMIT_MS);
    return () => window.clearTimeout(timer);
  }, [key, active, windowId]);

  const refetchInterval = useCallback<PollInterval<TData>>(
    (query) => {
      if (!key || !active) {
        return false;
      }
      // The first evaluation can come before the effect above has run.
      if (startedAt.current === 0) {
        startedAt.current = Date.now();
      }
      const count = query.state.dataUpdateCount;
      firstCount.current ??= count;
      return pollDelay({
        pollAfterMs: pollAfter(query.state.data),
        attempt: count - firstCount.current,
        elapsedMs: Date.now() - startedAt.current,
        hidden: typeof document !== 'undefined' && document.visibilityState === 'hidden',
      });
    },
    [key, active, pollAfter],
  );

  const restart = useCallback(() => {
    startedAt.current = 0;
    firstCount.current = null;
    setRound((value) => value + 1);
  }, []);

  return { refetchInterval, stillWorking: active && expired === windowId, restart };
}
